/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useModal } from '../../../contexts/ModalContext'
import { eosApiService } from '../../../services/api/api.eos'
import { getTask } from '../../../services/api/tasks'
import { getPdfUrlInShowFile } from '../../../services/api/files'
import { validateFileFactory } from '../../../services/factory/validateFileFactory'
import { TaskService, WorkflowStepType, TaskStatus } from '../../../services/task.service'
import { EOS_WORKFLOW_STEP_TYPE } from '../../../constants/eos'
import { toast } from '../../../services/toast'
import { useEsignature } from '../../../hooks/useEsignature'
import { PdfViewer } from '../../PdfViewer/PdfViewer'
import RcmEsignatureModal from '../../esignature/RcmEsignatureModal'
import AckCompletedDialog from './AckCompletedDialog'
import type { EosTask, EosPaginatedResponse, EosTaskFileEntry } from './types'
import type { ExtendedTask } from '../../../services/task.service'
import styled from 'styled-components'

// ─── Styled Components (matching legacy CSS) ─────────────────────────────

const TaskContainer = styled.div`
  min-height: 600px !important;
  > div.d-flex {
    background-color: white;
    border: 1px solid lightgrey;
  }
`

const InfoFrame = styled.div`
  background-color: white;
  border: 1px solid lightgrey;
  text-align: center;
  font-weight: bold;
  font-size: 1.1em;
  padding: 20px;
`

const TaskFrame = styled.div`
  padding: 15px;
`

const PdfFrame = styled.div`
  border: 1px solid lightgrey;
`

// ─── Constants ───────────────────────────────────────────────────────────

const PAGE_SIZE = 10

const REFRESH_REPEAT_COUNT = 20
const REFRESH_TIMEOUT = 1000

/**
 * EOS-specific WorkflowStepTypes that may not be in the standard WorkflowStepType const.
 * Legacy lego code references ACKNOWLEDGE_EOS, APPROVE_EOS, and DOWNLOAD_EOS.
 */
const EOS_STEP_TYPES = {
  ACKNOWLEDGE_EOS: EOS_WORKFLOW_STEP_TYPE.ACKNOWLEDGE_EOS,
  APPROVE_EOS: WorkflowStepType.APPROVE_EOS,
  DOWNLOAD_EOS: EOS_WORKFLOW_STEP_TYPE.DOWNLOAD_EOS
} as const

// ─── Helpers ─────────────────────────────────────────────────────────────

function isAcknowledgeOrApproveStep(stepType: string): boolean {
  return [EOS_STEP_TYPES.ACKNOWLEDGE_EOS, EOS_STEP_TYPES.APPROVE_EOS].includes(
    stepType as typeof EOS_STEP_TYPES.ACKNOWLEDGE_EOS | typeof EOS_STEP_TYPES.APPROVE_EOS
  )
}

// ─── Component ───────────────────────────────────────────────────────────

const TasksDashboard: React.FC = () => {
  const { t } = useTranslation()
  const { showModal } = useModal()
  const { signAction, showDsoModal, esignMessage, handleDsoSubmit, handleDsoClose } = useEsignature()

  const [tasksAvailable, setTasksAvailable] = useState<EosTask[]>([])
  const [nrTasksDisplayed, setNrTasksDisplayed] = useState(2)
  const [afterFirstLoad, setAfterFirstLoad] = useState(false)
  const hasNextPage = useRef<boolean>(false)
  const listQuery = useRef({ pageStart: 0, pageSize: PAGE_SIZE })

  // Stable refs to avoid re-creating callbacks on every render
  const validateFileRef = useRef(validateFileFactory())
  const showModalRef = useRef(showModal)
  showModalRef.current = showModal
  const signActionRef = useRef(signAction)
  signActionRef.current = signAction

  // ─── Data Loading ────────────────────────────────────────────────────

  const postprocessTask = useCallback((task: EosTask): void => {
    if (isAcknowledgeOrApproveStep(task.workflowStepType)) {
      const fileEntry: EosTaskFileEntry | undefined = task.files?.[0]
      const file = fileEntry?.draftFsi || fileEntry?.targetFsi
      if (file) {
        task.pdfInfo = { fsiId: file.id }
        task.pdfSrc = {
          service: () => {
            return getPdfUrlInShowFile(file.id, -1, 0, null)
          }
        }
        task.downloadFn = () => {
          validateFileRef.current.downloadPdf(file.id, true)
        }
      }
    }
  }, [])

  const postprocessTasks = useCallback(
    (tasks: EosTask[]): void => {
      tasks.forEach((task) => postprocessTask(task))
    },
    [postprocessTask]
  )

  const loadTasks = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      eosApiService
        .getUserTasks(listQuery.current)
        .then((resp: unknown) => {
          const data = resp as EosPaginatedResponse<EosTask>
          const tasks: EosTask[] = data.elements || []
          postprocessTasks(tasks)

          if (listQuery.current.pageStart === 0) {
            setTasksAvailable(tasks)
          } else {
            setTasksAvailable((prev) => prev.concat(tasks))
          }

          setAfterFirstLoad(true)
          hasNextPage.current = data.hasNextPage ?? false
          resolve()
        })
        .catch(reject)
    })
  }, [postprocessTasks])

  const checkFocusedTask = useCallback((): void => {
    // In rcm-new, route params would be accessed via React Router.
    // For now, check URL search params for taskId
    const urlParams = new URLSearchParams(window.location.search)
    const taskId = urlParams.get('taskId')
    if (!taskId) return

    // Clear the param from URL without navigation
    const url = new URL(window.location.href)
    url.searchParams.delete('taskId')
    window.history.replaceState({}, '', url.toString())

    getTask(taskId)
      .then((task) => {
        if (task.status === TaskStatus.APPROVED) {
          toast.error('EOS_DASHBOARD_TASK_LINK_COMPLETED_ERROR_MSG')
        } else if (task.status === TaskStatus.CANCELLED) {
          toast.error('EOS_DASHBOARD_TASK_LINK_CANCELLED_ERROR_MSG')
        }
      })
      .catch(() => {
        toast.error('EOS_DASHBOARD_TASK_LINK_EXPIRED_ERROR_MSG')
      })
  }, [])

  // Run only once on mount — loadTasks and checkFocusedTask are stable
  // (their deps are all refs or empty), but we use an empty array to be explicit.
  useEffect(() => {
    loadTasks()
    checkFocusedTask()
  }, [])

  // ─── Task Actions ────────────────────────────────────────────────────

  const reloadAllTasksRecursive = useCallback(
    (pagePosition: number, finalPagePosition: number): void => {
      if (pagePosition <= finalPagePosition) {
        listQuery.current = { pageStart: pagePosition, pageSize: PAGE_SIZE }
        loadTasks().then(() => {
          reloadAllTasksRecursive(pagePosition + 1, finalPagePosition)
        })
      }
    },
    [loadTasks]
  )

  const tryTaskRefresh = useCallback(
    (taskId: string, resolve: (task: EosTask) => void, reject: () => void, counter: number): void => {
      getTask(taskId)
        .then((resp) => {
          const updatedTask = resp as unknown as EosTask
          if (updatedTask.files && updatedTask.files[0] && updatedTask.files[0].targetFsi) {
            resolve(updatedTask)
          } else if (counter < REFRESH_REPEAT_COUNT) {
            setTimeout(() => {
              tryTaskRefresh(taskId, resolve, reject, counter + 1)
            }, REFRESH_TIMEOUT)
          } else {
            reject()
          }
        })
        .catch(() => {
          reject()
        })
    },
    []
  )

  const taskRefreshRecursive = useCallback(
    (taskId: string): Promise<EosTask> => {
      return new Promise<EosTask>((resolve, reject) => {
        tryTaskRefresh(taskId, resolve, reject, 0)
      })
    },
    [tryTaskRefresh]
  )

  const approveTask = useCallback(
    (task: EosTask): Promise<void> => {
      const extendedTask = task as unknown as ExtendedTask
      const req = TaskService.approveTask(task.id, '', '', extendedTask, {}, undefined, signActionRef.current)
      req.then(() => {
        reloadAllTasksRecursive(0, listQuery.current.pageStart)
      })
      return req
    },
    [reloadAllTasksRecursive]
  )

  const completeTask = useCallback(
    (task: EosTask): void => {
      if (task.workflowStepType === EOS_STEP_TYPES.DOWNLOAD_EOS) {
        if (task.eosDownloadMediaId) {
          validateFileRef.current
            .safeDownloadOriginal(task.eosDownloadMediaId)
            .then(() => {
              toast.success('EOS_MEDIA_DOWNLOAD_MEDIA_SUCCESS_MSG')
              approveTask(task)
            })
            .catch((error: { data?: { message?: string } }) => {
              toast.error(error?.data?.message || 'Download failed')
            })
        }
      } else {
        approveTask(task).then(() => {
          const refreshPromise =
            task.workflowStepType === EOS_STEP_TYPES.ACKNOWLEDGE_EOS
              ? getTask(task.id).then((t) => t as unknown as EosTask)
              : taskRefreshRecursive(task.id)

          refreshPromise.then(
            (updatedTask: EosTask) => {
              postprocessTask(updatedTask)
              showModalRef.current(<AckCompletedDialog task={updatedTask} />, { keepParentModalsOpen: true })
            },
            () => {
              toast.error('EOS_DASHBOARD_TASK_REGENERATION_FAILED_MSG')
            }
          )
        })
      }
    },
    [approveTask, taskRefreshRecursive, postprocessTask]
  )

  // ─── Pagination ──────────────────────────────────────────────────────

  const showMoreTasks = useCallback((): void => {
    if (tasksAvailable.length > nrTasksDisplayed) {
      if (nrTasksDisplayed === 2) {
        setNrTasksDisplayed(PAGE_SIZE)
      } else {
        setNrTasksDisplayed(nrTasksDisplayed + PAGE_SIZE)
      }
      if (hasNextPage.current) {
        listQuery.current = {
          pageStart: listQuery.current.pageStart + 1,
          pageSize: PAGE_SIZE
        }
        loadTasks()
      }
    }
  }, [tasksAvailable.length, nrTasksDisplayed, loadTasks])

  // ─── Render ──────────────────────────────────────────────────────────

  return (
    <div>
      <div>
        <h2 className="mt0">{t('MY_TASKS')}</h2>
      </div>
      <div className="row">
        {afterFirstLoad && tasksAvailable.length === 0 ? (
          <div className="col-xs-12">
            <InfoFrame>{t('EOS_DASHBOARD_TASKS_NO_TASKS_MSG')}</InfoFrame>
          </div>
        ) : (
          tasksAvailable.map((task, taskIndex) => {
            if (taskIndex >= nrTasksDisplayed) return null
            return (
              <TaskContainer key={task.id} className="col-xs-12 col-sm-6 d-flex mb20">
                <TaskFrame className="d-flex flex-column align-left align-self-stretch flex-grow">
                  <div className="d-flex flex-column align-left align-self-start align-self-stretch flex-grow w100p">
                    <div className="d-flex flex-column align-left align-self-start align-self-stretch flex-grow w100p">
                      <h3 className="mt0 align-left">
                        {isAcknowledgeOrApproveStep(task.workflowStepType)
                          ? t('EOS_TASK_ACKNOWLEDGE_ALT_TITLE')
                          : task.name}
                      </h3>

                      {isAcknowledgeOrApproveStep(task.workflowStepType) && task.pdfSrc && (
                        <PdfFrame className="d-flex align-self-stretch flex-grow">
                          <PdfViewer
                            src={task.pdfSrc}
                            thumbnailId={task.thumbnailId}
                            downloadFunction={task.downloadFn}
                            printLog={task.pdfInfo}
                            className="doc-pdf-container d-flex align-self-stretch flex-grow"
                          />
                        </PdfFrame>
                      )}

                      <div className="align-left">
                        {task.workflowStepType === EOS_STEP_TYPES.DOWNLOAD_EOS && (
                          <div
                            className="mt5"
                            dangerouslySetInnerHTML={{
                              __html: t('EOS_DASHBOARD_TASKS_COMPLETE_TASK_DOWNLOAD_INFO')
                            }}
                          />
                        )}
                        <div className="mt5">
                          <label>{t('EMTF_MD_TRIAL')}</label>
                          <div>{task.studyName}</div>
                        </div>
                        {task.siteName && (
                          <div className="mt5">
                            <label>{t('EMTF_MD_SITE')}</label>
                            <div>{task.siteName}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-column align-self-end align-right">
                    <button
                      data-action="confirm"
                      className="btn btn-primary text-capitalize"
                      onClick={() => completeTask(task)}
                    >
                      {t('EOS_DASHBOARD_TASKS_COMPLETE_TASK_BTN')}
                    </button>
                  </div>
                </TaskFrame>
              </TaskContainer>
            )
          })
        )}
      </div>

      {tasksAvailable.length > nrTasksDisplayed && (
        <div className="d-flex mt20">
          <button
            data-action="show-more"
            className="btn btn-second text-capitalize"
            id="8bbbc510-0a06-4839-92be-84abae2a6e0d"
            onClick={showMoreTasks}
          >
            {t('EOS_DASHBOARD_TASKS_SHOW_MORE_TASKS_BTN')}
          </button>
        </div>
      )}

      {/* E-Signature Modal (for DSO/RCM authentication) */}
      {showDsoModal && (
        <RcmEsignatureModal onSubmit={handleDsoSubmit} onClose={handleDsoClose} esignMessage={esignMessage} />
      )}
    </div>
  )
}

export default TasksDashboard
